import React, {useMemo} from "react";
import { blue, lightBlue, grey, red } from '@mui/material/colors'

import {
    getDayOfYearLabel,
    getSeason,
    isLeapYear,
    partitionData,
    generateStatisticsByKey,
    createDayOfMonthLabels,
    trimLabels,
    getSeasonLabel,
    extractAvailableSeasons,
    generateCumulative,
    objectify
} from "../utils";
import HighchartsReact from "highcharts-react-official";
import Toolbar, {useToolbar} from "./Toolbar";
import Stack from "@mui/material/Stack";
import {useTranslation} from "react-i18next";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";

const getHnData = (data, x) => {
    return data[x]?.hn ?? 0;
}

const getHnMean = (data, x) => {
    return data[x]?.hn?.mean ?? 0;
}


const HNCumGraph = ({data: originalData, station}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();

    const data = useMemo(() => (originalData.map(({timestamp, ...rest}) => {
            return {
                timestamp,
                label: getDayOfYearLabel(timestamp),
                season: getSeason(timestamp),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );
    const toolbarState = useToolbar(availableSeasons);

    const selectedSeason = toolbarState.selectedSeason;
    const historyYearFrom = toolbarState.historyFrom;
    const historyYearTo = toolbarState.historyTo;
    const historyLength = historyYearTo - historyYearFrom + 1;

    const [selectedData, historicData] = partitionData(selectedSeason, historyYearFrom, historyYearTo, data);

    const currentData = objectify(selectedData, 'label');
    // Use the historyLength to generate statistics because we want to ensure that the mean is computed
    // over the same number of seasons for each day (some day may be missing in some seasons).
    const historicStats = generateStatisticsByKey(historicData, ['hn'], 'label', historyLength);

    const withLeapYears = availableSeasons.some(y => isLeapYear(y) + 1);
    const labels = createDayOfMonthLabels(withLeapYears);

    const trimmedLabels = trimLabels(labels, {...currentData, ...historicStats});

    const currentCumData = generateCumulative(trimmedLabels, currentData, getHnData)
    const historicCumData = generateCumulative(trimmedLabels, historicStats, getHnMean)

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `${station?.code ?? 'station'}_hn_cumulative`
        },
        title: {
            text: `${station?.code ?? ''} - ${station?.name ?? ''} (${station?.elevation ?? '----'} m)`
        },
        subtitle: {
            text: t('hnCumGraph.title')
        },
        series: [
            {
                data: trimmedLabels.map(label => historicStats[label]?.hn?.min ?? null),
                type: 'area',
                name: t('hnCumGraph.legend.hnMin'),
                color: grey[700],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
            {
                data: trimmedLabels.map(label => historicStats[label]?.hn?.mean ?? null),
                type: 'area',
                name: t('hnCumGraph.legend.hnMean'),
                color: grey[500],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
            {
                data: trimmedLabels.map(label => historicStats[label]?.hn?.max ?? null),
                type: 'area',
                name: t('hnCumGraph.legend.hnMax'),
                color: grey[300],
                fillOpacity: 0.1,
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 2
                }
            },
            {
                data: trimmedLabels.map(label => currentData[label]?.hn ?? null),
                type: 'column',
                name: t('hnCumGraph.legend.hnSeason', {season: getSeasonLabel(selectedSeason)}),
                color: lightBlue[500],
                yAxis: 0,
            },
            {
                data: trimmedLabels.map(label => currentCumData[label] ?? null),
                type: 'line',
                name: t('hnCumGraph.legend.hnCum', {season: getSeasonLabel(selectedSeason)}),
                color: blue[900],
                yAxis: 1,
                marker: {
                    enabled: false
                }
            },
            {
                data: trimmedLabels.map(label => historicCumData[label] ?? null),
                type: 'line',
                name: t('hnCumGraph.legend.hnCumMean', {season: getSeasonLabel(selectedSeason)}),
                color: red[500],
                yAxis: 1,
                marker: {
                    enabled: false
                }
            }
        ],
        xAxis: {
            categories: trimmedLabels
        },
        yAxis: [
            { // First y-axis
                title: {
                    text: 'HN [cm]'
                }
            },
            { // First y-axis
                title: {
                    text: 'HN cumulata [cm]',
                },
                opposite: true
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
                let tooltip = '<b>' + this.x + '</b>';
                this.points.forEach(point => {
                    tooltip += '<br/><span style="color:' + point.series.color + '">●</span> ' + point.series.name + ': <b>' + Math.round(point.y) + '</b>';
                });
                return tooltip;
            }
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false // Do not connect null points
                },
                area: {
                    connectNulls: false // Do not connect null points
                },
                states: {
                    inactive: {
                        opacity: 1 // Prevent fading by keeping opacity at 1
                    }
                }
            }
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: 500,
            ...chartLogoOptions,
        },
        ...creditsOptions,
    }

    return <Stack>
        <Toolbar {...toolbarState} availableSeasons={availableSeasons} />
        <HighchartsReact
            highcharts={Highcharts}
            options={options}
        />
    </Stack>
}

export default HNCumGraph;